import { makeStyles } from "@material-ui/core";
import React, { useRef, useState, useEffect } from "react";
import FullscreenEcharts from "./FullscreenEcharts";
import { REPORT_FONT, useReportFonts } from "../../reportFonts";
import {
  TH_BG,
  TH_TEXT,
  TH_BORDER,
  TH_BG_BAND,
} from "../../reportTableConfig";
import {
  CHART_PY,
  CHART_CY,
  CHART_BUDGET,
  AD_RED,
} from "../../reportColorPalette";

export const PL_PAGE_BG = "#F4F5F7";
export const PL_SURFACE = "#FFFFFF";
export const PL_INK = "#111111";
export const PL_SUBTLE = "#374151";
export const PL_MUTED = "#1F2937";
export const PL_BORDER = "rgba(0,0,0,0.08)";
export const PL_BORDER_LT = "rgba(0,0,0,0.14)";
export const PL_POS_C = "#0F9D7A";
export const PL_NEG_C = AD_RED;
export const PL_PY_C = CHART_PY;
export const PL_CY_C = CHART_CY;
export const PL_BUD_C = CHART_BUDGET;
export const PL_FONT = REPORT_FONT;
export const PL_SURFACE_2  = "#F8F9FB";
export const PL_SURFACE_3  = "#F0F2F5";
/* ── Shared HTML table header tokens (from reportTableConfig) ── */
export const PL_TH_BG     = TH_BG;
export const PL_TH_TEXT   = TH_TEXT;
export const PL_TH_BORDER = TH_BORDER;
/** Matches Executive Summary KPI row height (label + value + 2 variance lines) */
export const PL_KPI_CARD_MIN_HEIGHT = 100;

const SURFACE = PL_SURFACE;
const KPI_CARD_MIN = PL_KPI_CARD_MIN_HEIGHT;
const SURFACE_2 = PL_SURFACE_2;
const SURFACE_3 = PL_SURFACE_3;
const PAGE_BG = PL_PAGE_BG;
const PY_C = PL_PY_C;
const CY_C = PL_CY_C;
const BUD_C = PL_BUD_C;
const POS_C = PL_POS_C;
const NEG_C = PL_NEG_C;
const INK = PL_INK;
const MUTED = PL_MUTED;
const SUBTLE = PL_SUBTLE;
const BORDER = PL_BORDER;
const BORDER_LT = PL_BORDER_LT;
const FONT_DISPLAY = PL_FONT;
const FONT_MONO = PL_FONT;
const FONT_BODY = PL_FONT;

export const usePlByProgramStyles = makeStyles(() => ({
  "@keyframes pulse": {
    "0%, 100%": { opacity: 1 },
    "50%":       { opacity: 0.35 },
  },
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to:   { opacity: 1, transform: "translateY(0)"    },
  },
  "@media (prefers-reduced-motion: reduce)": {
    "*, *::before, *::after": {
      animationDuration: "0.01ms !important",
      animationIterationCount: "1 !important",
      transitionDuration: "0.01ms !important",
    },
  },

  root: {
    minHeight: "100%",
    fontFamily: FONT_BODY,
    color: INK,
    backgroundColor: PAGE_BG,
    backgroundImage: `
      radial-gradient(ellipse 90% 55% at 50% -5%, rgba(245,158,11,0.05) 0%, transparent 60%),
      radial-gradient(ellipse 50% 40% at 0% 100%,  rgba(37,99,235,0.04) 0%, transparent 50%)
    `,
    paddingBottom: 32,
  },
  container: {
    padding: 0,
  },

  /* ── Hero header ── */
  pageHeader: {
    position: "relative",
    overflow: "hidden",
    margin: "0 0 20px",
    padding: "28px 36px",
    borderRadius: 16,
    background: "linear-gradient(rgb(11, 37, 69) 0%, rgb(17, 49, 91) 100%)",
    border: "1px solid rgba(255,255,255,0.09)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 24,
  },
  pageHeaderGrid: {
    position: "absolute",
    inset: 0,
    backgroundImage: `repeating-linear-gradient(
      -55deg,
      rgba(255,255,255,0.018) 0px,
      rgba(255,255,255,0.018) 1px,
      transparent 1px,
      transparent 28px
    )`,
    pointerEvents: "none",
  },
  pageHeaderGlow: {
    position: "absolute",
    inset: 0,
    background: `
      radial-gradient(ellipse 55% 90% at 100% 50%, rgba(99,102,241,0.07) 0%, transparent 60%),
      radial-gradient(ellipse 40% 60% at 0% 100%, rgba(245,158,11,0.05) 0%, transparent 55%)
    `,
    pointerEvents: "none",
  },
  headerBody: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "flex-start",
    gap: 20,
    flex: 1,
    animation: "$fadeUp 0.6s ease both",
  },
  headerIconWrap: {
    flexShrink: 0,
    width: 52,
    height: 52,
    borderRadius: 14,
    background: "rgba(52,211,153,0.10)",
    border: "1px solid rgba(52,211,153,0.22)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerLeft: {
    display: "flex",
    flexDirection: "column",
  },
  headerEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.20em",
    textTransform: "uppercase",
    color: "#F59E0B",
    marginBottom: 14,
    fontFamily: FONT_MONO,
    background: "rgba(245,158,11,0.10)",
    border: "1px solid rgba(245,158,11,0.22)",
    borderRadius: 6,
    padding: "4px 10px 4px 8px",
    width: "fit-content",
    "&::before": {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: "#F59E0B",
      flexShrink: 0,
    },
  },
  headerTitleRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 8,
  },
  headerTitleText: {
    fontSize: 34,
    fontWeight: 900,
    letterSpacing: "-0.04em",
    color: "#F0F6FF",
    lineHeight: 1.05,
    fontFamily: FONT_DISPLAY,
    margin: 0,
  },
  headerTitleSep: {
    fontSize: 28,
    color: "rgba(255,255,255,0.16)",
    fontWeight: 200,
    lineHeight: 1,
  },
  headerTitleAccent: {
    fontSize: 26,
    fontWeight: 400,
    letterSpacing: "-0.025em",
    color: "#94A3B8",
    fontFamily: FONT_DISPLAY,
    lineHeight: 1.05,
  },
  headerInlineMeta: {
    fontSize: 12,
    fontWeight: 400,
    color: "#7E97B0",
    fontFamily: FONT_BODY,
    marginBottom: 12,
    letterSpacing: "0.01em",
  },
  headerSub: {
    fontSize: 13.5,
    fontWeight: 400,
    color: "#8FACC4",
    maxWidth: 520,
    lineHeight: 1.72,
    fontFamily: FONT_BODY,
    marginBottom: 18,
    "& strong": { color: "#E2E8F0", fontWeight: 600 },
  },
  headerTags: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  headerTag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 500,
    color: "#A8BED4",
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.13)",
    borderRadius: 6,
    padding: "3px 10px",
    fontFamily: FONT_BODY,
  },
  headerTagDivider: {
    color: "rgba(255,255,255,0.30)",
    fontSize: 14,
    userSelect: "none",
    marginTop: -1,
  },
  headerMeta: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 12,
    animation: "$fadeUp 0.6s 0.1s ease both",
    minWidth: 190,
  },
  headerMetaTop: {
    fontSize: 10,
    fontWeight: 500,
    color: "#7E97B0",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontFamily: FONT_MONO,
  },
  headerBadge: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    fontWeight: 700,
    color: "#0B2545",
    background: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
    borderRadius: 10,
    padding: "11px 22px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    fontFamily: FONT_MONO,
    boxShadow: "0 4px 20px rgba(245,158,11,0.35), 0 1px 0 rgba(255,255,255,0.15) inset",
  },
  liveDot: {
    display: "inline-block",
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#0B2545",
    animation: "$pulse 2s ease-in-out infinite",
    flexShrink: 0,
  },
  headerDate: {
    fontSize: 12,
    fontWeight: 500,
    color: "#8FACC4",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.13)",
    borderRadius: 8,
    padding: "7px 16px",
    fontFamily: FONT_MONO,
    letterSpacing: "0.03em",
  },

  /* ── Section card ── */
  sectionCard: {
    background: SURFACE,
    borderRadius: 16,
    border: `1px solid ${BORDER}`,
    marginBottom: 20,
    overflow: "hidden",
    animation: "$fadeUp 0.5s ease both",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  },
  sectionToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "18px 28px",
    borderBottom: "2px solid #F0F2F5",
    background: SURFACE,
    flexWrap: "wrap",
  },
  sectionTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flex: 1,
    minWidth: 140,
  },
  sectionMetricBadge: {
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    fontFamily: FONT_MONO,
    color: "#4338CA",
    background: "linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(129,140,248,0.08) 100%)",
    border: "1px solid rgba(99,102,241,0.28)",
    borderRadius: 6,
    padding: "5px 10px",
    lineHeight: 1,
  },
  sectionAccent: {
    width: 4,
    height: 36,
    borderRadius: 3,
    background: "linear-gradient(180deg, #6366F1 0%, #A78BFA 100%)",
    boxShadow: "0 0 12px rgba(99,102,241,0.35)",
    flexShrink: 0,
  },
  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  sectionInfoWrap: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    "&:hover $sectionInfoText, &:focus-within $sectionInfoText": {
      display: "block",
    },
  },
  sectionInfoIcon: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#4338CA",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    padding: 0,
    cursor: "help",
    fontFamily: FONT_BODY,
  },
  sectionInfoText: {
    display: "none",
    position: "absolute",
    left: 0,
    top: "calc(100% + 6px)",
    zIndex: 20,
    background: "#1F2937",
    color: "#fff",
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.45,
    whiteSpace: "normal",
    width: 280,
    padding: "8px 10px",
    borderRadius: 6,
    pointerEvents: "none",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: INK,
    letterSpacing: "-0.02em",
    fontFamily: FONT_DISPLAY,
    lineHeight: 1,
  },
  sectionSubtitle: {
    fontSize: 12,
    fontWeight: 400,
    color: SUBTLE,
    marginTop: 4,
    fontFamily: FONT_BODY,
  },
  toolbarFilters: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  filterMeta: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 36,
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    overflow: "hidden",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    flexShrink: 0,
  },
  filterMetaKey: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4A55A2",
    padding: "0 14px",
    background: "#F0F2FF",
    height: "100%",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontFamily: FONT_BODY,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  filterMetaVal: {
    fontSize: 13,
    fontWeight: 400,
    color: INK,
    padding: "0 16px",
    background: "#FFFFFF",
    fontFamily: FONT_BODY,
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    height: "100%",
  },

  /* ── Filter dropdowns (pill: lavender label + white value) ── */
  filterGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 36,
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    flexShrink: 0,
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": {
      borderColor: "#C7D2FE",
      boxShadow: "0 3px 10px rgba(74,85,162,0.12)",
    },
  },
  filterGroupLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4A55A2",
    padding: "0 14px",
    background: "#F0F2FF",
    height: "100%",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    userSelect: "none",
    fontFamily: FONT_BODY,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  filterGroupSelect: {
    border: "none",
    outline: "none",
    backgroundColor: "#FFFFFF",
    fontSize: 13,
    fontWeight: 400,
    color: INK,
    padding: "0 28px 0 14px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: 100,
    height: "100%",
    fontFamily: FONT_BODY,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748B' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    "&:focus": { outline: "none" },
    "&:focus-visible": {
      outline: "none",
      outlineOffset: -2,
    },
  },

  /* ── BU filter bar ── */
  buFilterBar: {
    display: "flex",
    alignItems: "center",
    padding: "11px 28px",
    background: "linear-gradient(90deg, #EEF1F8 0%, #F4F6FB 50%, #EEF1F8 100%)",
    borderTop: "1px solid #DDE3EE",
    borderBottom: "1px solid #DDE3EE",
    gap: 12,
  },
  buBarLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#111827",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    fontFamily: FONT_MONO,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: 1,
    "&::before": {
      content: '""',
      display: "block",
      width: 10,
      height: 10,
      borderRadius: 3,
      background: "linear-gradient(135deg, #6366F1 0%, #818CF8 100%)",
      flexShrink: 0,
    },
  },
  buValueBadge: {
    fontSize: 12,
    fontWeight: 700,
    color: TH_BG_BAND,
    background: "#FFFFFF",
    borderRadius: 8,
    padding: "6px 16px",
    border: "1px solid #C7D2FE",
    fontFamily: FONT_MONO,
    boxShadow: "0 1px 4px rgba(99,102,241,0.10)",
    letterSpacing: "0.01em",
  },

  /* ── KPI grid ── */
  kpiArea: {
    padding: "20px 24px 24px",
    borderBottom: "1px solid #CED9E5",
    background: "#E4EBF3",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 16,
    alignItems: "stretch",
    gridAutoRows: `minmax(${KPI_CARD_MIN}px, auto)`,
  },
  kpiGrid6: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: 16,
    alignItems: "stretch",
    gridAutoRows: `minmax(${KPI_CARD_MIN}px, auto)`,
  },
  kpiGridItem: {
    display: "flex",
    flexDirection: "column",
    minHeight: KPI_CARD_MIN,
    width: "100%",
    height: "100%",
  },
  kpiCard: {
    position: "relative",
    background: "#FFFFFF",
    borderRadius: 14,
    border: "1px solid #E2E8F0",
    padding: "14px 16px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 14,
    overflow: "visible",
    boxSizing: "border-box",
    width: "100%",
    flex: "1 1 auto",
    minHeight: KPI_CARD_MIN,
    boxShadow: "0 2px 6px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.04)",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0, top: 0, bottom: 0,
      width: 3,
      background: "linear-gradient(180deg, #6366F1 0%, rgba(99,102,241,0.20) 100%)",
      borderRadius: "14px 0 0 14px",
    },
    "&:hover": {
      borderColor: "#C7D2FE",
      boxShadow: "0 8px 24px rgba(99,102,241,0.10), 0 2px 6px rgba(0,0,0,0.06)",
      transform: "translateY(-2px)",
    },
  },
  kpiLeft: {
    flex: "0 0 auto",
    display: "flex",
    flexDirection: "column",
  },
  kpiCornerBaseline: {
    position: "absolute",
    top: 10,
    right: 12,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    zIndex: 1,
  },
  kpiCornerBaselineLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: "#4A55A2",
    fontFamily: PL_FONT,
    background: "#F0F2FF",
    borderRadius: 4,
    padding: "2px 6px",
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    lineHeight: 1.2,
  },
  kpiCornerBaselineValue: {
    fontSize: 11,
    fontWeight: 700,
    color: INK,
    fontFamily: PL_FONT,
    whiteSpace: "nowrap",
    lineHeight: 1.2,
  },
  kpiRight: {
    display: "grid",
    gridTemplateColumns: "52px max-content max-content",
    columnGap: 6,
    rowGap: 8,
    alignItems: "center",
    paddingTop: 16,
    paddingLeft: 12,
    marginLeft: 10,
    borderLeft: "1px solid #E8ECF2",
    flexShrink: 0,
  },
  kpiCardLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#111827",
    textTransform: "uppercase",
    letterSpacing: "0.13em",
    marginBottom: 8,
    fontFamily: FONT_MONO,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  kpiCardValue: {
    fontSize: 28,
    fontWeight: 700,
    color: "#060D1F",
    lineHeight: 1,
    letterSpacing: "-0.04em",
    fontFamily: FONT_MONO,
    display: "flex",
    alignItems: "baseline",
    gap: 2,
  },
  kpiCardSign: {
    fontSize: 15,
    fontWeight: 600,
    color: "#1F2937",
    fontFamily: FONT_MONO,
  },
  kpiCardUnit: {
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    fontFamily: FONT_MONO,
    marginLeft: 2,
  },
  kpiVarLine: {
    display: "contents",
  },
  kpiVarPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    borderRadius: 4,
    padding: "2px 5px",
    fontSize: 10,
    fontWeight: 700,
    fontFamily: FONT_MONO,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    flexShrink: 0,
    minWidth: 0,
  },
  kpiVarPillCurrency: {
    minWidth: 64,
  },
  kpiVarPillPct: {
    minWidth: 58,
  },
  kpiVarDesc: {
    fontSize: 9,
    fontWeight: 500,
    color: SUBTLE,
    lineHeight: 1.3,
    fontFamily: FONT_BODY,
    whiteSpace: "nowrap",
    width: 52,
    flexShrink: 0,
  },

  /* ── Charts row ── */
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 14,
    padding: "20px 24px",
    background: "rgba(0,0,0,0.03)",
    borderTop: `1px solid ${BORDER}`,
  },
  chartPanel: {
    background: SURFACE_2,
    borderRadius: 12,
    border: `1px solid ${BORDER}`,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: 320,
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      borderColor: BORDER_LT,
      boxShadow: "0 6px 24px rgba(15, 23, 42, 0.08)",
    },
  },
  chartPanelTitle: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 40px 10px 14px",
    fontSize: 10,
    fontWeight: 600,
    color: "#FFFFFF",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontFamily: FONT_MONO,
    background: TH_BG_BAND,
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    flexShrink: 0,
  },
  /** Nested in uppercase chart titles so "BUs" does not render as "BUS". */
  chartTitleKeepCase: {
    textTransform: "none",
  },
  chartTitleFsBtn: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    border: "none",
    background: "transparent",
    padding: 0,
    lineHeight: 0,
    cursor: "pointer",
    color: "#FFFFFF",
    transition: "color 0.15s",
    "&:hover": { color: "#F59E0B" },
    "&:focus-visible": {
      outline: "2px solid #F59E0B",
      outlineOffset: 2,
      borderRadius: 4,
    },
  },
  chartPanelBody: {
    flex: 1,
    display: "flex",
    padding: "6px 6px 2px",
    minHeight: 0,
  },
  chartPanelStack: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "12px 24px 16px",
    background: "rgba(0,0,0,0.03)",
    borderTop: `1px solid ${BORDER}`,
  },
  chartPanelBridge: {
    height: 380,
  },
  chartPanelLg: {
    height: 590,
  },
  chartPanelBodyBridge: {
    flex: 1,
    display: "flex",
    padding: "4px 4px 0",
    minHeight: 0,
    overflow: "hidden",
  },
  sectionCardDelayed: {
    animationDelay: "0.08s",
  },

  /* ── Bridge / chart-only reports ── */
  filterBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "18px 28px",
    borderBottom: "2px solid #F0F2F5",
    background: SURFACE,
    flexWrap: "wrap",
  },
  versionCompareRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  versionVs: {
    fontSize: 13,
    fontWeight: 600,
    color: PL_MUTED,
    letterSpacing: "0.04em",
    flexShrink: 0,
    userSelect: "none",
  },
  chartCard: {
    background: SURFACE,
    borderRadius: 16,
    border: `1px solid ${BORDER}`,
    margin: "0 0 20px",
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    padding: "16px 20px 20px",
  },
  chartsTop: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
    marginBottom: 16,
  },
  chartsBottom: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
    marginBottom: 16,
  },
  chartCardCenter: { gridColumn: "2" },
}));

export function PlSectionInfoTip({ label, text, classes }) {
  return (
    <div className={classes.sectionInfoWrap}>
      <button
        type="button"
        className={classes.sectionInfoIcon}
        aria-label={label}
      >
        i
      </button>
      <span className={classes.sectionInfoText} role="tooltip">
        {text}
      </span>
    </div>
  );
}

export function PlFilterGroup({ label, options, value, onChange, classes }) {
  return (
    <div className={classes.filterGroup}>
      <span className={classes.filterGroupLabel}>{label}</span>
      <select
        className={classes.filterGroupSelect}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => {
          const val = o != null && typeof o === "object" ? o.value : o;
          const lbl = o != null && typeof o === "object" ? o.label : o;
          return (
            <option key={val} value={val} style={{ background: PL_SURFACE, color: PL_INK }}>
              {lbl}
            </option>
          );
        })}
      </select>
    </div>
  );
}

export function PlMultiSelect({ label, options, value, onChange, classes, maxSelected }) {
  const [open, setOpen] = useState(false);
  const [noneSelected, setNoneSelected] = useState(false);
  const containerRef = useRef(null);
  const valueRef     = useRef(value);
  const limited = Number.isFinite(maxSelected) && maxSelected > 0;

  useEffect(() => {
    if (!open) return;
    function handleDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleDown);
    return () => document.removeEventListener("mousedown", handleDown);
  }, [open]);

  // Reset noneSelected when the parent updates value externally (new reference)
  useEffect(() => {
    if (value !== valueRef.current) {
      valueRef.current = value;
      setNoneSelected(false);
    }
  }, [value]);

  const isAll        = !limited && !noneSelected && value.length === 0;
  const allExplicit  = !limited && !noneSelected && options.length > 0 && value.length >= options.length;
  const atLimit      = limited && value.length >= maxSelected;
  const displayVal   = noneSelected
    ? "None"
    : (isAll || allExplicit)
      ? "All"
      : value.length === 1
        ? value[0]
        : `${value.length} selected`;

  function handleAll() {
    if (limited) return;
    if (isAll || allExplicit) {
      setNoneSelected(true);            // deselect all — parent value stays [], no onChange
    } else if (noneSelected) {
      setNoneSelected(false);           // re-select All — parent already has [], no onChange
    } else {
      setNoneSelected(false);
      onChange([]);
    }
  }

  function handleItem(opt) {
    setNoneSelected(false);
    if (isAll) {
      onChange(options.filter((o) => o !== opt));
    } else if (value.includes(opt)) {
      const next = value.filter((v) => v !== opt);
      onChange(next);
    } else {
      if (atLimit) return;
      const next = [...value, opt];
      onChange(!limited && next.length >= options.length ? [] : next);
    }
  }

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <div
        className={classes.filterGroup}
        onClick={() => setOpen((o) => !o)}
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        <span className={classes.filterGroupLabel}>{label}</span>
        <span
          className={classes.filterGroupSelect}
          style={{ display: "flex", alignItems: "center", userSelect: "none", pointerEvents: "none" }}
        >
          {displayVal}
        </span>
      </div>
      {open && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 9999,
            background: PL_SURFACE, borderRadius: 10, border: `1px solid ${PL_BORDER_LT}`,
            boxShadow: "0 8px 24px rgba(0,0,0,0.14)", minWidth: 170, padding: "4px 0",
            maxHeight: 260, overflowY: "auto",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {!limited && (
            <label style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 14px",
              cursor: "pointer", fontSize: 13, fontWeight: 600, color: PL_INK,
              borderBottom: `1px solid ${PL_BORDER}`, fontFamily: PL_FONT,
            }}>
              <input
                type="checkbox"
                checked={isAll || allExplicit}
                onChange={handleAll}
                style={{ cursor: "pointer", accentColor: "#6366F1", flexShrink: 0 }}
              />
              All
            </label>
          )}
          {options.map((opt) => {
            const checked = isAll || value.includes(opt);
            const disabled = atLimit && !checked;
            return (
              <label key={opt} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "6px 14px",
                cursor: disabled ? "not-allowed" : "pointer",
                fontSize: 13,
                color: disabled ? MUTED : PL_INK,
                fontFamily: PL_FONT,
                opacity: disabled ? 0.55 : 1,
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => handleItem(opt)}
                  style={{ cursor: disabled ? "not-allowed" : "pointer", accentColor: "#6366F1", flexShrink: 0 }}
                />
                {opt}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function PlKpiCard({
  label,
  value,
  varPct,
  varDiff,
  varDesc,
  var2Pct,
  var2Diff,
  var2Desc,
  baselineLabel,
  baselineValue,
  cornerBaselineLabel,
  cornerBaselineValue,
  valueColor,
  classes,
}) {
  const fmt = (p) => {
    if (p == null) return null;
    if (p >= 0) return `+${p.toFixed(1)}%`;
    return `(${Math.abs(p).toFixed(1)}%)`;
  };
  const fmtDiff = (raw) => {
    if (raw == null || Number.isNaN(raw)) return null;
    const m = raw / 1_000_000;
    const abs = Math.abs(m).toFixed(1);
    return m < 0 ? `($${abs})` : `$${abs}`;
  };
  const clr = (p) => (p == null ? PL_SUBTLE : p >= 0 ? PL_POS_C : PL_NEG_C);
  const bg = (p) =>
    p == null ? "transparent" : p >= 0 ? "rgba(15,157,122,0.12)" : "rgba(214,69,80,0.12)";
  const arrow = (p) => (p >= 0 ? "▲" : "▼");
  const f1 = fmt(varPct);
  const f2 = fmt(var2Pct);
  const d1 = fmtDiff(varDiff);
  const d2 = fmtDiff(var2Diff);

  const renderVarLine = (pct, diff, desc, fPct, fDiff, key) => {
    if (!fPct && !fDiff) return null;
    const signVal = diff != null ? diff : pct;
    return (
      <div key={key} className={classes.kpiVarLine}>
        <span className={classes.kpiVarDesc}>{desc || ""}</span>
        {fDiff ? (
          <span
            className={`${classes.kpiVarPill} ${classes.kpiVarPillCurrency}`}
            style={{ color: clr(signVal), background: bg(signVal) }}
          >
            {fDiff}M
          </span>
        ) : (
          <span />
        )}
        {fPct ? (
          <span
            className={`${classes.kpiVarPill} ${classes.kpiVarPillPct}`}
            style={{ color: clr(pct), background: bg(pct) }}
          >
            {pct != null && <span>{arrow(pct)}</span>} {fPct}
          </span>
        ) : (
          <span />
        )}
      </div>
    );
  };

  return (
    <div className={classes.kpiGridItem}>
      <div className={classes.kpiCard}>
        {cornerBaselineLabel && cornerBaselineValue != null && (
          <div className={classes.kpiCornerBaseline}>
            <span className={classes.kpiCornerBaselineLabel}>{cornerBaselineLabel}</span>
            <span className={classes.kpiCornerBaselineValue}>${cornerBaselineValue}M</span>
          </div>
        )}
        <div className={classes.kpiLeft}>
          <div className={classes.kpiCardLabel}>{label}</div>
          <div className={classes.kpiCardValue} style={valueColor ? { color: valueColor } : undefined}>
            {(() => {
              const raw = value == null ? "" : String(value).trim();
              const paren = raw.match(/^\((.+)\)$/);
              const negNum = raw.match(/^-(.+)$/);
              if (paren || negNum) {
                const abs = paren ? paren[1] : negNum[1];
                return (
                  <>
                    <span>(</span>
                    <span className={classes.kpiCardSign} style={valueColor ? { color: valueColor } : undefined}>$</span>
                    {abs}
                    <span className={classes.kpiCardUnit} style={valueColor ? { color: valueColor } : undefined}>M</span>
                    <span>)</span>
                  </>
                );
              }
              return (
                <>
                  <span className={classes.kpiCardSign} style={valueColor ? { color: valueColor } : undefined}>$</span>
                  {raw}
                  <span className={classes.kpiCardUnit} style={valueColor ? { color: valueColor } : undefined}>M</span>
                </>
              );
            })()}
          </div>
        </div>
        {(f1 || d1 || f2 || d2) && (
          <div className={classes.kpiRight}>
            {renderVarLine(varPct, varDiff, varDesc, f1, d1, "var1")}
            {renderVarLine(var2Pct, var2Diff, var2Desc, f2, d2, "var2")}
          </div>
        )}
        {baselineLabel && baselineValue != null && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            paddingLeft: 12, marginLeft: 8, borderLeft: "1px solid #E8ECF2",
            flexShrink: 0, gap: 3,
          }}>
            <span style={{
              fontSize: 9, fontWeight: 700, color: "#4A55A2", fontFamily: PL_FONT,
              background: "#F0F2FF", borderRadius: 4, padding: "2px 6px",
              letterSpacing: "0.10em", textTransform: "uppercase",
            }}>
              {baselineLabel}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: INK, fontFamily: PL_FONT, whiteSpace: "nowrap" }}>
              ${baselineValue}M
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function PlKpiRow({
  budget,
  cyForecast,
  ytdActuals,
  etc,
  kpiValues,
  varData,
  metricChoice,
  classes,
  splitBaselineCard = false,
}) {
  const kpi = kpiValues || { budget, cyForecast, ytdActuals, etc };
  const { budget: b, cyForecast: cy, ytdActuals: ytd, etc: e, blForecast, blYtd, blEtc } = kpi;
  const blLabel = metricChoice === "Vs. Prior Forecast" ? "PF" : "PY";
  const varDesc = `Vs. ${blLabel}`;

  if (splitBaselineCard) {
    return (
      <div className={classes.kpiArea}>
        <div className={classes.kpiGrid}>
          <PlKpiCard label="Budget" value={b} classes={classes} />
          <PlKpiCard
            label="CY Forecast"
            value={cy}
            cornerBaselineLabel={blLabel}
            cornerBaselineValue={blForecast}
            varPct={varData?.cyVsPY?.pct}
            varDiff={varData?.cyVsPY?.diff}
            varDesc={varDesc}
            var2Pct={varData?.cyVsBud?.pct}
            var2Diff={varData?.cyVsBud?.diff}
            var2Desc="Vs. Budget"
            classes={classes}
          />
          <PlKpiCard
            label="YTD Actuals"
            value={ytd}
            cornerBaselineLabel={blLabel}
            cornerBaselineValue={blYtd}
            varPct={varData?.ytdVsPY?.pct}
            varDiff={varData?.ytdVsPY?.diff}
            varDesc={varDesc}
            var2Pct={varData?.ytdVsBud?.pct}
            var2Diff={varData?.ytdVsBud?.diff}
            var2Desc="Vs. Budget"
            classes={classes}
          />
          <PlKpiCard
            label="ETC"
            value={e}
            cornerBaselineLabel={blLabel}
            cornerBaselineValue={blEtc}
            varPct={varData?.etcVsPY?.pct}
            varDiff={varData?.etcVsPY?.diff}
            varDesc={varDesc}
            var2Pct={varData?.etcVsBud?.pct}
            var2Diff={varData?.etcVsBud?.diff}
            var2Desc="Vs. Budget"
            classes={classes}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.kpiArea}>
      <div className={classes.kpiGrid}>
        <PlKpiCard label="Budget" value={b} classes={classes} />
        <PlKpiCard
          label="CY Forecast"
          value={cy}
          baselineLabel={blLabel}
          baselineValue={blForecast}
          varPct={varData?.cyVsPY?.pct}
          varDiff={varData?.cyVsPY?.diff}
          varDesc={varDesc}
          var2Pct={varData?.cyVsBud?.pct}
          var2Diff={varData?.cyVsBud?.diff}
          var2Desc="Vs. Budget"
          classes={classes}
        />
        <PlKpiCard
          label="YTD Actuals"
          value={ytd}
          baselineLabel={blLabel}
          baselineValue={blYtd}
          varPct={varData?.ytdVsPY?.pct}
          varDiff={varData?.ytdVsPY?.diff}
          varDesc={varDesc}
          var2Pct={varData?.ytdVsBud?.pct}
          var2Diff={varData?.ytdVsBud?.diff}
          var2Desc="Vs. Budget"
          classes={classes}
        />
        <PlKpiCard
          label="ETC"
          value={e}
          baselineLabel={blLabel}
          baselineValue={blEtc}
          varPct={varData?.etcVsPY?.pct}
          varDiff={varData?.etcVsPY?.diff}
          varDesc={varDesc}
          var2Pct={varData?.etcVsBud?.pct}
          var2Diff={varData?.etcVsBud?.diff}
          var2Desc="Vs. Budget"
          classes={classes}
        />
      </div>
    </div>
  );
}

/** Y-axis config for million-scale bar/line charts — avoids duplicate integer labels on small ranges */
export function millionsYAxisTicks(...valueSeries) {
  const values = valueSeries
    .flat(2)
    .map(Number)
    .filter((n) => Number.isFinite(n));
  const maxAbs = values.length ? Math.max(...values.map((n) => Math.abs(n)), 0) : 0;
  const decimals = maxAbs > 0 && maxAbs < 10 ? 1 : 0;
  const config = {
    axisLabel: {
      formatter: (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return "";
        return n.toFixed(decimals);
      },
    },
  };
  if (decimals > 0) config.minInterval = 0.5;
  return config;
}

/** Format $M values for chart labels/tooltips — negatives as ($x.x)M */
export function formatMillionsLabel(p) {
  const n = Number(p?.value ?? p);
  if (!Number.isFinite(n)) return "";
  const abs = Math.abs(n).toFixed(1);
  return n < 0 ? `($${abs}M)` : `$${abs}M`;
}

/** Data labels for $M line charts (always outside) */
export function millionsChartLabel(overrides = {}) {
  return {
    show: true,
    position: "top",
    distance: 4,
    fontSize: 9,
    fontWeight: 600,
    color: PL_MUTED,
    fontFamily: PL_FONT,
    formatter: (p) => {
      const n = Number(p?.value);
      if (!Number.isFinite(n) || n === 0) return "";
      return formatMillionsLabel(n);
    },
    ...overrides,
  };
}

/**
 * Bar series data with labels inside the bar (white, rotated to fit),
 * or outside in the series color when the bar is too short (also rotated
 * to avoid overlap between adjacent PY/CY/Budget columns).
 * Outside labels always sit toward the plot interior (`top`) so they never
 * spill into x-axis category labels.
 */
export function millionsBarSeriesData(values, barColor, opts = {}) {
  const nums = (values || []).map((v) => Number(v));
  const finiteAbs = nums.map(Math.abs).filter((n) => Number.isFinite(n));
  const maxAbs =
    opts.maxAbs ?? (finiteAbs.length ? Math.max(...finiteAbs, 0) : 0);
  const minInsideAbs =
    opts.minInsideAbs ?? Math.max(maxAbs * (opts.minInsideRatio ?? 0.14), 0.01);
  const fontSize = opts.fontSize ?? 9;
  const insideRotate = opts.insideRotate ?? opts.rotate ?? 90;
  const outsideRotate = opts.outsideRotate ?? opts.rotate ?? 90;

  return nums.map((n) => {
    if (!Number.isFinite(n) || n === 0) {
      return { value: n, label: { show: false } };
    }
    const inside = Math.abs(n) >= minInsideAbs;
    if (inside) {
      return {
        value: n,
        label: {
          show: true,
          position: "inside",
          distance: 0,
          rotate: insideRotate,
          fontSize,
          fontWeight: 700,
          color: "#FFFFFF",
          fontFamily: PL_FONT,
          formatter: formatMillionsLabel,
        },
      };
    }
    /* Always anchor above the bar tip (into the chart). Using "bottom" for
       negatives drops labels into the x-axis; verticalAlign bottom + align
       left with rotate 90 grows the text up away from the axis. */
    return {
      value: n,
      label: {
        show: true,
        position: "top",
        distance: opts.outsideDistance ?? 8,
        rotate: outsideRotate,
        align: "left",
        verticalAlign: "bottom",
        fontSize,
        fontWeight: 700,
        color: barColor,
        fontFamily: PL_FONT,
        backgroundColor: "rgba(255,255,255,0.92)",
        borderRadius: 2,
        padding: [1, 2],
        formatter: formatMillionsLabel,
      },
    };
  });
}

function formatBridgeTooltipValue(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n < 0 ? `(${Math.abs(n).toFixed(1)})` : n.toFixed(1);
}

/** Nice y-axis scale for diverging $M bars — prevents duplicate tick labels on small ranges */
function computeDivergingBarYAxis(values) {
  const nums = (values || [])
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n));
  if (!nums.length) {
    return {
      min: 0,
      max: 1,
      interval: 0.5,
      formatLabel: (v) => String(v),
    };
  }

  let dataMin = Math.min(0, ...nums);
  let dataMax = Math.max(0, ...nums);
  const peak = Math.max(...nums.map((n) => Math.abs(n)), 0);
  if (peak === 0) {
    return {
      min: 0,
      max: 1,
      interval: 0.5,
      formatLabel: (v) => (v === 0 ? "0" : String(v)),
    };
  }
  const span = Math.max(dataMax - dataMin, peak * 0.5);
  const pad = span * 0.12;
  dataMin -= pad;
  dataMax += pad;

  const rawInterval = Math.max(span / 4, peak / 8, 0.1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawInterval)));
  const normalized = rawInterval / magnitude;
  let niceUnit = 10;
  if (normalized <= 1) niceUnit = 1;
  else if (normalized <= 2) niceUnit = 2;
  else if (normalized <= 5) niceUnit = 5;
  const interval = niceUnit * magnitude;

  const min = Math.floor(dataMin / interval) * interval;
  const max = Math.ceil(dataMax / interval) * interval;

  const decimals =
    interval >= 1
      ? 0
      : Math.max(0, String(interval).split(".")[1]?.length ?? 1);

  const formatLabel = (v) => {
    const n = Number(v.toFixed(decimals));
    if (n === 0) return "0";
    return decimals === 0 ? String(n) : n.toFixed(decimals);
  };

  return { min, max, interval, formatLabel };
}

/** Per-contract v1 metric in $M for bridge chart tooltips. */
export function bridgeContractMetricV1M(contract, kind) {
  const nested = contract?.[kind];
  const varianceKey = kind === "revenue" ? "revenueVariance" : "grossMarginVariance";
  const v1Key = kind === "revenue" ? "revenueV1" : "grossMarginV1";
  const v2Key = kind === "revenue" ? "revenueV2" : "grossMarginV2";
  const variance = contract?.[varianceKey];

  let raw;
  if (nested != null && typeof nested === "object") {
    raw = nested.v1 ?? null;
    if (raw == null && nested.v2 != null && variance != null) raw = nested.v2 + variance;
  } else if (contract?.[v1Key] != null) {
    raw = contract[v1Key];
  } else if (typeof nested === "number") {
    raw = nested;
  } else if (contract?.[v2Key] != null && variance != null) {
    raw = contract[v2Key] + variance;
  }

  if (raw == null || Number.isNaN(Number(raw))) return null;
  return +(Number(raw) / 1_000_000).toFixed(2);
}

function buildBridgeTooltipHtml({
  contractName,
  varianceValue,
  contractId,
}) {
  const row = (label, value) =>
    `<span style="text-align:right;white-space:normal;">${label}</span>` +
    `<span style="text-align:left;white-space:normal;max-width:280px;">${value}</span>`;
  return (
    `<div style="display:grid;grid-template-columns:auto auto;column-gap:20px;row-gap:4px;` +
    `font-family:Roboto, Helvetica, Arial, sans-serif;color:${PL_INK};font-size:12px;font-weight:500;">` +
    row("Contract Name", contractName || "—") +
    row("Variance", varianceValue ?? "—") +
    row("Contract Id", contractId || "—") +
    `</div>`
  );
}

/** Diverging bar chart option — matches Executive Summary axis/tooltip styling */
export function buildPlDivergingBarOption({
  categories,
  values,
  contractIds = [],
}) {
  const posBar = {
    type: "linear",
    x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: "#14B8A0" },
      { offset: 1, color: PL_POS_C },
    ],
  };
  const negBar = {
    type: "linear",
    x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: "#E86872" },
      { offset: 1, color: PL_NEG_C },
    ],
  };
  const data = values.map((v, i) => {
    const n = Number(v);
    const pos = Number.isFinite(n) && n >= 0;
    return {
      value: v,
      contractName: categories[i] ?? "",
      contractId: contractIds[i] ?? "",
      itemStyle: {
        color: pos ? posBar : negBar,
        borderRadius: pos ? [3, 3, 0, 0] : [0, 0, 3, 3],
      },
      label: {
        show: Number.isFinite(n) && n !== 0,
        position: pos ? "top" : "bottom",
        distance: 4,
        fontSize: 9,
        fontWeight: 700,
        color: pos ? PL_POS_C : PL_NEG_C,
        fontFamily: PL_FONT,
        formatter: formatMillionsLabel,
      },
    };
  });

  const yAxisScale = computeDivergingBarYAxis(values);

  return {
    backgroundColor: "transparent",
    grid: { top: 28, right: 8, bottom: 80, left: 16, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow", shadowStyle: { color: "rgba(255,255,255,0.03)" } },
      backgroundColor: PL_SURFACE_3,
      borderColor: PL_BORDER_LT,
      borderWidth: 1,
      padding: [10, 14],
      textStyle: { fontSize: 12, color: PL_INK, fontFamily: PL_FONT, fontWeight: 500 },
      extraCssText: "border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,0.12);",
      formatter: (params) => {
        const p = params[0];
        const raw = p?.data?.value ?? p?.value;
        return buildBridgeTooltipHtml({
          contractName: p?.data?.contractName ?? p?.axisValue ?? "",
          varianceValue: formatBridgeTooltipValue(raw),
          contractId: p?.data?.contractId ?? "",
        });
      },
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        fontSize: 11,
        color: PL_MUTED,
        fontWeight: 500,
        rotate: 35,
        fontFamily: PL_FONT,
        interval: 0,
        margin: 8,
        formatter: (v) => (v && v.length > 12 ? `${v.slice(0, 11)}…` : v),
      },
    },
    yAxis: {
      type: "value",
      min: yAxisScale.min,
      max: yAxisScale.max,
      interval: yAxisScale.interval,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: "#F1F5F9", type: "dashed" } },
      axisLabel: {
        fontSize: 10,
        color: PL_SUBTLE,
        fontFamily: PL_FONT,
        fontWeight: 500,
        formatter: yAxisScale.formatLabel,
      },
    },
    series: [{ type: "bar", data, barMaxWidth: 22 }],
  };
}

function PlChartFullscreenIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

/** Chart title bars are CSS-uppercase; keep the plural s so BUs does not render as BUS. */
export function ChartTitleBuPlural({ prefix, classes }) {
  return (
    <>
      {prefix} BU<span className={classes.chartTitleKeepCase}>s</span>
    </>
  );
}

/** Chart panel with dark title bar — same shell as Executive Summary charts */
export function PlChartPanel({
  title,
  option,
  classes,
  panelClassName,
  bodyClassName,
  onEvents,
  sideContent,
}) {
  const chartRef = useRef(null);
  return (
    <div className={`${classes.chartPanel} ${panelClassName || ""}`}>
      <div className={classes.chartPanelTitle}>
        {title}
        <button
          type="button"
          className={classes.chartTitleFsBtn}
          onClick={() => chartRef.current?.toggleFullscreen?.()}
          aria-label="View Fullscreen"
          title="View Fullscreen"
        >
          <PlChartFullscreenIcon />
        </button>
      </div>
      <div className={bodyClassName || classes.chartPanelBody}>
        {sideContent}
        <FullscreenEcharts
          ref={chartRef}
          showButton={false}
          option={option}
          height="100%"
          style={{ flex: 1, minHeight: 0 }}
          notMerge
          onEvents={onEvents}
        />
      </div>
    </div>
  );
}

/** Simple KPI card (bridge / single-value rows) — reuses PlKpiCard for identical height */
export function PlSimpleKpiCard({ label, title, value, classes, formatValue, rawValue }) {
  const cardLabel = label ?? title;
  const display =
    formatValue != null
      ? formatValue(value)
      : value < 0
        ? `(${Math.abs(Number(value)).toFixed(1)})`
        : Number(value).toFixed(1);
  const valueColor =
    rawValue == null ? undefined : rawValue > 0 ? PL_POS_C : rawValue < 0 ? PL_NEG_C : undefined;
  return <PlKpiCard label={cardLabel} value={display} valueColor={valueColor} classes={classes} />;
}

export function PlPageHeader({
  classes,
  eyebrow = "P&L by Program",
  title,
  accent,
  meta,
  metaTop,
  badge = "Live Forecast",
  date = null,
}) {
  const showMetaTop =
    metaTop && !(date && /^Actuals through/i.test(String(metaTop).trim()));

  return (
    <header className={classes.pageHeader}>
      <div className={classes.pageHeaderGrid} />
      <div className={classes.pageHeaderGlow} />
      <div className={classes.headerBody}>
        <div className={classes.headerIconWrap}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#34D399" aria-hidden="true">
            <path d="M12 2 L13.8 10.2 L22 12 L13.8 13.8 L12 22 L10.2 13.8 L2 12 L10.2 10.2 Z" />
          </svg>
        </div>
        <div className={classes.headerLeft}>
          <div className={classes.headerEyebrow}>{eyebrow}</div>
          <div className={classes.headerTitleRow}>
            <h1 className={classes.headerTitleText}>{title}</h1>
            {accent && (
              <>
                <span className={classes.headerTitleSep}>·</span>
                <span className={classes.headerTitleAccent}>{accent}</span>
              </>
            )}
          </div>
          {meta && <div className={classes.headerInlineMeta}>{meta}</div>}
        </div>
      </div>
      <div className={classes.headerMeta}>
        {showMetaTop && <span className={classes.headerMetaTop}>{metaTop}</span>}
        <span className={classes.headerBadge}>
          <span className={classes.liveDot} />
          {badge}
        </span>
        {date && <span className={classes.headerDate}>{date}</span>}
      </div>
    </header>
  );
}
